import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from "react";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../hooks/useNotificationsContext";
import { DietaryRequirement, Preorder } from "../../../../../types/bookings";
import DietaryRequirementsModal from "./DietaryRequirementsModal";
import {
  addOrUpdateBooking,
  mapBookingV4ToState
} from "../../../../../APIs/Booking/UpdateBooking";
import { EDSButton } from "../../../../eds/EDSButton";
import AddEditPreOrderItemModal from "./AddEditPreOrderItemModal";
import {
  addPreOrderItem,
  updatePreOrderItem,
  deletePreOrderItem,
  updateServiceCharge,
  updatePreOrderComments,
  PreOrderItemFormData
} from "../../../../../APIs/Booking/PreOrderOperations";
import { ExternalLink, ChevronDown } from "lucide-react";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { Label } from "../../../../ui/label";
import {
  getPreOrderTotals,
  getPreOrderTotalsRaw
} from "./SelectedBookingPreOrdersController";
import { getCurrencySymbol } from "../../../../../utils/number";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "../../../../ui/dialog";
import { GUEST_COLOR_PALETTE, normaliseGuestName } from "./PreOrderTypes";
import RedirectingToPaymentsDialog from "./RedirectingToPaymentsDialog";
import { CustomerPreordersSection } from "./CustomerPreorders";
import PreOrderItemsSection from "./PreOrderItemsSection";
import DietaryRequirementsSection from "./DietaryRequirementsSection";

type SelectedBookingPreOrdersProps = {
  navigateToPaymentsWithAmount?: (amount: number) => void;
};

const SelectedBookingPreOrders = ({
  navigateToPaymentsWithAmount
}: SelectedBookingPreOrdersProps) => {
  const diaryContext = useDiaryContext();
  const { selectedBookingV4, venueConfig } = diaryContext;
  const notificationsContext = useNotificationsContext();
  const { userCanReadPreOrders, userCanUpdatePreOrders } =
    useBookingPermissions();
  const [isDietaryModalOpen, setIsDietaryModalOpen] = useState(false);
  const [selectedDietaryRequirement, setSelectedDietaryRequirement] = useState<
    DietaryRequirement | undefined
  >();
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [selectedPreOrder, setSelectedPreOrder] = useState<
    Preorder | undefined
  >();
  const [editingServiceCharge, setEditingServiceCharge] = useState<
    string | null
  >(null);
  const [serviceChargeValue, setServiceChargeValue] = useState<number>(0);
  const [editingComments, setEditingComments] = useState<string | null>(null);
  const [commentsValue, setCommentsValue] = useState<string>("");
  const [editingNotes, setEditingNotes] = useState<boolean>(false);
  const [notesValue, setNotesValue] = useState<string>("");
  const [isSavingNotes, setIsSavingNotes] = useState<boolean>(false);

  const [isEditingMinSpend, setIsEditingMinSpend] = useState(false);
  const [minSpendAmount, setMinSpendAmount] = useState<number>(0);
  const [minSpendPerHead, setMinSpendPerHead] = useState<boolean>(false);
  const [isSavingMinSpend, setIsSavingMinSpend] = useState(false);
  const [isRedirectingToPayments, setIsRedirectingToPayments] = useState(false);
  const [pendingPaymentAmount, setPendingPaymentAmount] = useState<number>(0);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Display options for pre-order items (matching collins-ui)
  const [simpleView, setSimpleView] = useState(false);
  const [groupByType, setGroupByType] = useState(false);
  const [groupBySubType, setGroupBySubType] = useState(false);
  const [expandMenuItems, setExpandMenuItems] = useState(false);
  const [groupByTime, setGroupByTime] = useState(false);
  const [includeForNames, setIncludeForNames] = useState(false);

  const currency = venueConfig?.currency ?? "GBP";
  const currencySymbol = getCurrencySymbol(currency);

  const totalsRaw = useMemo(
    () => getPreOrderTotalsRaw(selectedBookingV4),
    [selectedBookingV4]
  );

  const preOrderTotals = useMemo(
    () => getPreOrderTotals(selectedBookingV4, currency),
    [selectedBookingV4, currency]
  );

  const remainingToPay = Math.max(totalsRaw.remainingToPay, 0);

  const computedMinSpend = useMemo(() => {
    if (minSpendPerHead && selectedBookingV4?.num_people) {
      return minSpendAmount * selectedBookingV4.num_people;
    }
    return minSpendAmount;
  }, [minSpendAmount, minSpendPerHead, selectedBookingV4?.num_people]);

  useEffect(() => {
    setIsEditingMinSpend(false);
  }, [selectedBookingV4?._id]);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const handleAddDietaryRequirement = () => {
    setSelectedDietaryRequirement(undefined);
    setSelectedIndex(undefined);
    setIsDietaryModalOpen(true);
  };

  const handleEditDietaryRequirement = (
    requirement: DietaryRequirement,
    index: number
  ) => {
    setSelectedDietaryRequirement(requirement);
    setSelectedIndex(index);
    setIsDietaryModalOpen(true);
  };

  const handleDeleteDietaryRequirement = async (index: number) => {
    if (!selectedBookingV4) return;

    const currentRequirements = selectedBookingV4.dietary_requirements ?? [];
    const updatedRequirements = [...currentRequirements];
    updatedRequirements.splice(index, 1);

    const viewBooking = mapBookingV4ToState(selectedBookingV4);

    await addOrUpdateBooking({
      booking: {
        ...viewBooking,
        dietaryRequirements: updatedRequirements
      },
      diaryContext
    });
  };

  const handleSaveDietaryRequirement = async (
    requirement: DietaryRequirement
  ) => {
    if (!selectedBookingV4) return;

    const currentRequirements = selectedBookingV4.dietary_requirements ?? [];
    const updatedRequirements = [...currentRequirements];

    if (selectedIndex !== undefined) {
      updatedRequirements[selectedIndex] = requirement;
    } else {
      updatedRequirements.push(requirement);
    }

    const viewBooking = mapBookingV4ToState(selectedBookingV4);

    await addOrUpdateBooking({
      booking: {
        ...viewBooking,
        dietaryRequirements: updatedRequirements
      },
      diaryContext
    });

    setIsDietaryModalOpen(false);
  };

  const formatDietaryRequirements = (
    requirement: DietaryRequirement
  ): string => {
    const dietTypes = Array.isArray(requirement.diet_types)
      ? requirement.diet_types
      : [];
    const allergies = Array.isArray(requirement.allergies)
      ? requirement.allergies
      : [];
    const items = [...dietTypes, ...allergies];
    return items.length > 0 ? items.join(", ") : "No requirements specified";
  };

  const handleAddPreOrderItem = () => {
    setSelectedPreOrder(undefined);
    setIsAddEditModalOpen(true);
  };

  const handleEditPreOrderItem = (preOrder: Preorder) => {
    setSelectedPreOrder(preOrder);
    setIsAddEditModalOpen(true);
  };

  const handleEditPreOrderItemById = (preOrderId: string) => {
    const preOrder = manualPreOrders.find((po) => po.id === preOrderId);
    if (preOrder) {
      handleEditPreOrderItem(preOrder);
    }
  };

  const handleSavePreOrderItem = async (itemData: PreOrderItemFormData) => {
    if (!selectedBookingV4) return;

    let result;
    if (selectedPreOrder) {
      result = await updatePreOrderItem(
        selectedBookingV4,
        selectedPreOrder.id,
        itemData
      );
    } else {
      result = await addPreOrderItem(selectedBookingV4, itemData);
    }

    if (result.booking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: result.booking
      });
    }

    setIsAddEditModalOpen(false);
    setSelectedPreOrder(undefined);
  };

  const handleDeletePreOrderItem = async (preOrderId: string) => {
    if (!selectedBookingV4) return;

    const result = await deletePreOrderItem(selectedBookingV4, preOrderId);

    if (result.booking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: result.booking
      });
    }
  };

  const handleEditServiceCharge = (
    preOrderId: string,
    currentPercentage: number
  ) => {
    setEditingServiceCharge(preOrderId);
    setServiceChargeValue(currentPercentage ?? 0);
  };

  const handleSaveServiceCharge = async (preOrderId: string) => {
    if (!selectedBookingV4) return;

    const result = await updateServiceCharge(
      selectedBookingV4,
      preOrderId,
      serviceChargeValue
    );

    if (result.booking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: result.booking
      });
    }

    setEditingServiceCharge(null);
  };

  const handleEditComments = (preOrderId: string, currentComments: string) => {
    setEditingComments(preOrderId);
    setCommentsValue(currentComments ?? "");
  };

  const handleSaveComments = async (preOrderId: string) => {
    if (!selectedBookingV4) return;

    const result = await updatePreOrderComments(
      selectedBookingV4,
      preOrderId,
      commentsValue
    );

    if (result.booking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: result.booking
      });
    }

    setEditingComments(null);
  };

  const handleEditNotes = () => {
    setEditingNotes(true);
    setNotesValue(selectedBookingV4?.notes ?? "");
  };

  const handleSaveNotes = async () => {
    if (!selectedBookingV4) return;

    setIsSavingNotes(true);

    try {
      const viewBooking = {
        ...mapBookingV4ToState(selectedBookingV4),
        notes: notesValue ?? ""
      };

      const result = await addOrUpdateBooking({
        booking: viewBooking,
        diaryContext
      });

      if (result && !result.unsaved) {
        diaryContext.setDiaryContext({
          selectedBookingV4: {
            ...selectedBookingV4,
            notes: notesValue ?? ""
          }
        });
        notificationsContext.addNotification({
          heading: "Notes saved successfully",
          autoClose: 3000,
          toastType: "success"
        });
      } else {
        notificationsContext.addNotification({
          heading: "Failed to save notes",
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to save notes",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsSavingNotes(false);
      setEditingNotes(false);
    }
  };

  const handleStartEditMinSpend = () => {
    setIsEditingMinSpend(true);
    setMinSpendAmount(selectedBookingV4?.min_spend?.amount ?? 0);
    setMinSpendPerHead(selectedBookingV4?.min_spend?.per_head ?? false);
  };

  const handleSaveMinSpend = async () => {
    if (!selectedBookingV4) return;

    setIsSavingMinSpend(true);

    try {
      const updatedBookingV4 = {
        ...selectedBookingV4,
        min_spend: {
          amount: minSpendAmount,
          per_head: minSpendPerHead,
          paid_amount: selectedBookingV4.min_spend?.paid_amount ?? 0
        }
      };

      const viewBooking = mapBookingV4ToState(updatedBookingV4);

      const result = await addOrUpdateBooking({
        booking: viewBooking,
        diaryContext
      });

      if (result && !result.unsaved) {
        diaryContext.setDiaryContext({
          selectedBookingV4: updatedBookingV4
        });
        notificationsContext.addNotification({
          heading: "Minimum spend saved successfully",
          autoClose: 3000,
          toastType: "success"
        });
        setIsEditingMinSpend(false);
      } else {
        notificationsContext.addNotification({
          heading: "Failed to save minimum spend",
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to save minimum spend",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsSavingMinSpend(false);
    }
  };

  const handleCancelMinSpend = () => {
    setIsEditingMinSpend(false);
    setMinSpendAmount(0);
    setMinSpendPerHead(false);
  };

  const handleAddPaymentClick = useCallback(
    (amount: number) => {
      if (!navigateToPaymentsWithAmount) return;
      setPendingPaymentAmount(amount);
      setIsRedirectingToPayments(true);
      // Clear any existing timeout before setting a new one
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      redirectTimeoutRef.current = setTimeout(() => {
        setIsRedirectingToPayments(false);
        navigateToPaymentsWithAmount(amount);
      }, 1500);
    },
    [navigateToPaymentsWithAmount]
  );

  const manualPreOrders =
    selectedBookingV4?.preorders?.filter(
      (preOrder) => !preOrder.status || preOrder.status !== "deleted"
    ) || [];

  // Group pre-order items based on display options (matching collins-ui logic)
  type GroupedItem = {
    type: string;
    subType: string;
    time: string;
    name: string;
    totalQuantity: number;
    totalCost: number;
    price: number;
    customers: Array<{ name: string; quantity: number; seat?: string }>;
    description?: string;
    menuId?: string;
    isHeading?: boolean;
    preOrderIds: string[];
  };

  type GroupedPreOrders = Record<string, GroupedItem[]>;

  const groupedPreOrderItems = useMemo((): GroupedPreOrders => {
    // Flatten all items from all pre-orders
    const allItems: Array<{
      item: (typeof manualPreOrders)[0]["items"][0];
      preOrderId: string;
      preOrderName: string;
    }> = [];

    manualPreOrders.forEach((preOrder) => {
      (preOrder.items ?? []).forEach((item) => {
        allItems.push({
          item,
          preOrderId: preOrder.id,
          preOrderName: preOrder.name
        });
      });
    });

    // Group items by unique key (time + id + price + options)
    const uniqueItems: Record<string, GroupedItem> = {};

    allItems.forEach(({ item, preOrderId }) => {
      const itemKey = `${groupByTime ? (item.time ?? "") : ""}-${item.id}-${item.price}-${item.options_string ?? ""}`;

      if (!uniqueItems[itemKey]) {
        uniqueItems[itemKey] = {
          type: item.type ?? "Other",
          subType: item.sub_type ?? "",
          time: item.time ?? "",
          name: item.name,
          totalQuantity: 0,
          totalCost: 0,
          price: item.price,
          customers: [],
          description: item.description,
          menuId: item.menu_id,
          preOrderIds: []
        };
      }

      uniqueItems[itemKey].totalQuantity += item.quantity;
      uniqueItems[itemKey].totalCost += item.total;

      if (!uniqueItems[itemKey].preOrderIds.includes(preOrderId)) {
        uniqueItems[itemKey].preOrderIds.push(preOrderId);
      }

      if (includeForNames && item.for_name) {
        uniqueItems[itemKey].customers.push({
          name: item.for_name,
          quantity: item.quantity,
          seat: item.seat
        });
      }
    });

    // Convert to array and group based on active filters
    const itemsArray = Object.values(uniqueItems);

    // Determine grouping strategy based on active filters (priority: type > subType > time > expandMenuItems)
    if (groupByType) {
      // Group by type
      const byType: GroupedPreOrders = {};

      itemsArray.forEach((item) => {
        const typeKey = item.type || "Other";
        if (!byType[typeKey]) {
          byType[typeKey] = [];
        }

        // Add sub-type heading if grouping by sub-type
        if (groupBySubType && item.subType) {
          const lastItem = byType[typeKey][byType[typeKey].length - 1];
          if (!lastItem || lastItem.subType !== item.subType) {
            byType[typeKey].push({
              type: item.type,
              subType: item.subType,
              time: "",
              name:
                item.subType.charAt(0).toUpperCase() + item.subType.slice(1),
              totalQuantity: 0,
              totalCost: 0,
              price: 0,
              customers: [],
              isHeading: true,
              preOrderIds: []
            });
          }
        }

        byType[typeKey].push(item);
      });

      return byType;
    }

    if (groupBySubType) {
      // Group by sub-type when type grouping is not active
      const bySubType: GroupedPreOrders = {};

      itemsArray.forEach((item) => {
        const subTypeKey = item.subType
          ? item.subType.charAt(0).toUpperCase() + item.subType.slice(1)
          : "Other";
        if (!bySubType[subTypeKey]) {
          bySubType[subTypeKey] = [];
        }
        bySubType[subTypeKey].push(item);
      });

      return bySubType;
    }

    if (groupByTime) {
      // Group by time
      const byTime: GroupedPreOrders = {};

      itemsArray.forEach((item) => {
        const timeKey = item.time || "No specified time";
        if (!byTime[timeKey]) {
          byTime[timeKey] = [];
        }
        byTime[timeKey].push(item);
      });

      return byTime;
    }

    if (expandMenuItems) {
      // Group by fixed price menu - use menu_id to identify items from fixed price menus
      const byMenu: GroupedPreOrders = {};

      itemsArray.forEach((item) => {
        // Use menu_id as the key for fixed price menu items, otherwise group as "Items"
        const menuKey = item.menuId ?? "Items";
        if (!byMenu[menuKey]) {
          byMenu[menuKey] = [];
        }
        byMenu[menuKey].push(item);
      });

      return byMenu;
    }

    // Return all items under a single "all" key
    return { all: itemsArray };
  }, [
    manualPreOrders,
    groupByType,
    groupBySubType,
    groupByTime,
    includeForNames,
    expandMenuItems
  ]);

  // Check if any display options are active (excluding includeForNames as it doesn't group)
  const hasActiveDisplayOptions =
    groupByType || groupBySubType || expandMenuItems || groupByTime;

  // Group pre-orders by selected criteria for Pretty View (preserves original pre-order objects)
  type GroupedPreOrdersForCards = Record<string, typeof manualPreOrders>;

  const groupedPreOrdersForCards = useMemo((): GroupedPreOrdersForCards => {
    if (!hasActiveDisplayOptions) {
      return { all: manualPreOrders };
    }

    const groups: GroupedPreOrdersForCards = {};

    manualPreOrders.forEach((preOrder) => {
      // Determine group key based on active filters (priority: type > subType > time)
      let groupKey = "Other";

      if (groupByType) {
        // Group by item type
        const firstItem = preOrder.items?.[0];
        groupKey = firstItem?.type ?? "Other";
      } else if (groupBySubType) {
        // Group by item sub-type
        const firstItem = preOrder.items?.[0];
        groupKey = firstItem?.sub_type
          ? firstItem.sub_type.charAt(0).toUpperCase() +
            firstItem.sub_type.slice(1)
          : "Other";
      } else if (expandMenuItems) {
        // Group by fixed price menu (using menu_id)
        const firstItem = preOrder.items?.[0];
        groupKey = firstItem?.menu_id ?? "Items";
      } else if (groupByTime) {
        // Group by time
        const firstItem = preOrder.items?.[0];
        groupKey = firstItem?.time ?? "No specified time";
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(preOrder);
    });

    return groups;
  }, [
    manualPreOrders,
    hasActiveDisplayOptions,
    groupByType,
    groupBySubType,
    expandMenuItems,
    groupByTime
  ]);

  const allNames = useMemo(() => {
    const names: string[] = [];
    manualPreOrders.forEach((po) => {
      const n = po.items?.[0]?.for_name;
      if (n) names.push(normaliseGuestName(n));
    });
    (selectedBookingV4?.dietary_requirements ?? []).forEach((r) => {
      if (r?.name) names.push(normaliseGuestName(r.name));
    });
    return names;
  }, [manualPreOrders, selectedBookingV4?.dietary_requirements]);

  const colorMap = useMemo(() => {
    const map = new Map<string, number>();
    let i = 0;
    for (const n of allNames) {
      if (!map.has(n)) {
        map.set(n, i % GUEST_COLOR_PALETTE.length);
        i++;
      }
    }
    return map;
  }, [allNames]);

  const guestCardClasses = (name?: string) => {
    if (!name) return "";
    const key = normaliseGuestName(name);
    const idx = colorMap.get(key);
    if (idx === undefined) return "";
    const { bg, border } = GUEST_COLOR_PALETTE[idx];
    return `${bg} ${border}`;
  };

  const renderNotesContent = () => {
    const hasNotes = selectedBookingV4?.notes;

    return (
      <button
        type="button"
        className="w-full border border-border rounded-lg p-3 bg-muted flex items-center cursor-pointer hover:bg-muted/80 transition-colors text-left"
        onClick={handleEditNotes}
        disabled={!userCanUpdatePreOrders}
        aria-label={hasNotes ? "Edit pre-order notes" : "Add pre-order notes"}
      >
        <p
          className={`text-sm flex-1 ${hasNotes ? "text-foreground" : "text-muted-foreground italic"}`}
        >
          {hasNotes
            ? selectedBookingV4.notes
            : "This booking does not have any notes yet"}
        </p>
        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 ml-auto" />
      </button>
    );
  };

  if (!userCanReadPreOrders) {
    return (
      <div className="BookingPreOrders">
        <div className="w-full mt-2 pb-8 flex flex-col items-center justify-center p-8">
          <p className="text-sm text-muted-foreground">
            You do not have permission to view pre-orders.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="BookingPreOrders mt-panel-padding flex flex-wrap">
      {/* Sections stacked vertically with full width */}
      <div className="w-full mt-2 pb-8 flex flex-col">
        {/* Summary Section */}
        <section className="py-4 px-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Summary</h3>
            <div className="flex items-center gap-2">
              {remainingToPay > 0 && navigateToPaymentsWithAmount && (
                <EDSButton
                  variant="default"
                  size="sm"
                  onClick={() => handleAddPaymentClick(remainingToPay)}
                >
                  Add payment
                  <ExternalLink className="ml-1 h-3 w-3" />
                </EDSButton>
              )}
              {userCanUpdatePreOrders &&
                !totalsRaw.hasMinSpend &&
                !isEditingMinSpend && (
                  <EDSButton
                    variant="default"
                    size="sm"
                    onClick={handleStartEditMinSpend}
                  >
                    Add minimum spend
                  </EDSButton>
                )}
              {userCanUpdatePreOrders &&
                totalsRaw.hasMinSpend &&
                !isEditingMinSpend && (
                  <EDSButton
                    variant="default"
                    size="sm"
                    onClick={handleStartEditMinSpend}
                  >
                    Edit minimum spend
                  </EDSButton>
                )}
            </div>
          </div>

          <div className="border border-border rounded-lg p-4 bg-muted/30">
            <div className="grid grid-cols-5 gap-3">
              {preOrderTotals.map((item) => {
                const isBalanceDue = item.description === "Balance due";
                const isRemainingSpend =
                  item.description === "Remaining to spend";

                return (
                  <div
                    key={item.description}
                    className="border border-border rounded-md p-2 bg-background shadow-sm"
                  >
                    <div className="text-xs text-muted-foreground mb-1">
                      {item.description}
                    </div>
                    <div
                      className={`text-sm font-medium ${
                        (isBalanceDue && totalsRaw.remainingToPay > 0) ||
                        (isRemainingSpend && totalsRaw.remainingSpend > 0)
                          ? "text-destructive"
                          : ""
                      }`}
                    >
                      {item.total}
                    </div>
                  </div>
                );
              })}
            </div>

            {isEditingMinSpend && (
              <div className="mt-3 p-3 border border-border rounded-lg bg-muted">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Label className="text-sm font-medium">Minimum spend</Label>
                    <div className="flex gap-2">
                      <EDSButton
                        variant={!minSpendPerHead ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMinSpendPerHead(false)}
                      >
                        Total
                      </EDSButton>
                      <EDSButton
                        variant={minSpendPerHead ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMinSpendPerHead(true)}
                      >
                        Per head
                      </EDSButton>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{currencySymbol}</span>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      value={minSpendAmount || ""}
                      onChange={(e) =>
                        setMinSpendAmount(parseFloat(e.target.value) || 0)
                      }
                      className="w-24"
                      autoComplete="off"
                    />
                    {minSpendPerHead && selectedBookingV4?.num_people && (
                      <span className="text-sm text-muted-foreground">
                        Total: {currencySymbol}
                        {computedMinSpend.toFixed(2)} (
                        {selectedBookingV4.num_people} guests)
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <EDSButton
                      variant="default"
                      size="sm"
                      onClick={handleSaveMinSpend}
                      disabled={isSavingMinSpend}
                    >
                      {isSavingMinSpend ? "Saving..." : "Save"}
                    </EDSButton>
                    <EDSButton
                      variant="outline"
                      size="sm"
                      onClick={handleCancelMinSpend}
                      disabled={isSavingMinSpend}
                    >
                      Cancel
                    </EDSButton>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Notes Section */}
        <section className="py-4 px-4">
          <h3 className="text-sm font-semibold mb-3">Notes</h3>
          {renderNotesContent()}
        </section>

        {/* Notes Edit Modal */}
        <Dialog open={editingNotes} onOpenChange={setEditingNotes}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Edit Notes</DialogTitle>
              <DialogDescription>
                Add or edit notes for this pre-order.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              <Textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                rows={6}
                placeholder="Add notes..."
                className="min-h-32 w-full"
                aria-label="Pre-order notes"
              />
              <div className="flex justify-end gap-2">
                <EDSButton
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingNotes(false)}
                  disabled={isSavingNotes}
                >
                  Cancel
                </EDSButton>
                <EDSButton
                  variant="default"
                  size="sm"
                  onClick={handleSaveNotes}
                  disabled={isSavingNotes}
                >
                  {isSavingNotes ? "Saving..." : "Save"}
                </EDSButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Items Section */}
        <PreOrderItemsSection
          simpleView={simpleView}
          setSimpleView={setSimpleView}
          groupByType={groupByType}
          setGroupByType={setGroupByType}
          groupBySubType={groupBySubType}
          setGroupBySubType={setGroupBySubType}
          expandMenuItems={expandMenuItems}
          setExpandMenuItems={setExpandMenuItems}
          groupByTime={groupByTime}
          setGroupByTime={setGroupByTime}
          includeForNames={includeForNames}
          setIncludeForNames={setIncludeForNames}
          manualPreOrders={manualPreOrders}
          groupedPreOrderItems={groupedPreOrderItems}
          groupedPreOrdersForCards={groupedPreOrdersForCards}
          hasActiveDisplayOptions={hasActiveDisplayOptions}
          currency={currency}
          guestCardClasses={guestCardClasses}
          editingServiceCharge={editingServiceCharge}
          serviceChargeValue={serviceChargeValue}
          setServiceChargeValue={setServiceChargeValue}
          setEditingServiceCharge={setEditingServiceCharge}
          handleSaveServiceCharge={handleSaveServiceCharge}
          handleEditServiceCharge={handleEditServiceCharge}
          editingComments={editingComments}
          commentsValue={commentsValue}
          setCommentsValue={setCommentsValue}
          setEditingComments={setEditingComments}
          handleSaveComments={handleSaveComments}
          handleEditComments={handleEditComments}
          handleEditPreOrderItem={handleEditPreOrderItem}
          handleEditPreOrderItemById={handleEditPreOrderItemById}
          handleDeletePreOrderItem={handleDeletePreOrderItem}
          handleAddPreOrderItem={handleAddPreOrderItem}
        />

        {/* Dietary Requirements Section */}
        <DietaryRequirementsSection
          dietaryRequirements={selectedBookingV4?.dietary_requirements}
          guestCardClasses={guestCardClasses}
          formatDietaryRequirements={formatDietaryRequirements}
          handleEditDietaryRequirement={handleEditDietaryRequirement}
          handleDeleteDietaryRequirement={handleDeleteDietaryRequirement}
          handleAddDietaryRequirement={handleAddDietaryRequirement}
        />

        {/* Customer Pre-Orders Section */}
        <CustomerPreordersSection />
      </div>

      <DietaryRequirementsModal
        open={isDietaryModalOpen}
        onOpenChange={setIsDietaryModalOpen}
        dietaryRequirement={selectedDietaryRequirement}
        onSave={handleSaveDietaryRequirement}
      />

      <AddEditPreOrderItemModal
        open={isAddEditModalOpen}
        onOpenChange={setIsAddEditModalOpen}
        onSave={handleSavePreOrderItem}
        existingPreOrder={selectedPreOrder}
        currency={currency}
      />

      <RedirectingToPaymentsDialog
        isOpen={isRedirectingToPayments}
        currencySymbol={currencySymbol}
        amount={pendingPaymentAmount}
      />
    </div>
  );
};

export default SelectedBookingPreOrders;
